import { ScrollSection } from "@/components/main-page/ScrollSection";
import { AgentActivityFeed } from "./AgentActivityFeed";

function IconLinkedIn() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

function IconX() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function IconGitHub() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
    </svg>
  );
}

const ICON_MAP: Record<string, () => React.JSX.Element> = {
  linkedin: IconLinkedIn,
  x: IconX,
  github: IconGitHub,
};

interface Member {
  name: string;
  role: string;
  location?: string;
  photo: string | null;
  initials: string;
  photoPosition?: string;
  badge?: string;
  bio?: string;
  links?: Record<string, string>;
  placeholder?: boolean;
}

const EXEC_TEAM: Member[] = [
  {
    name: "Ben Zhang",
    role: "Co-Founder, Technical",
    location: "London",
    bio: "15 years building technology for regulated financial services. Created over $100M in enterprise value across FCA-regulated fintech. Founded and exited a fintech agency.",
    photo: "/team-ben.jpg",
    initials: "BZ",
    photoPosition: "center top",
    links: {
      linkedin: "https://www.linkedin.com/in/benwk/",
      x: "https://x.com/benwkz",
      github: "https://github.com/benwk",
    },
  },
  {
    name: "Hugh Carnegie",
    role: "Co-Founder, Operations",
    location: "London",
    bio: "5 years leading operations at early-stage companies. 3 products to market, $5M ARR. Specialist in operational playbooks that scale.",
    photo: "/team-hugh.jpg",
    initials: "HC",
    photoPosition: "center 15%",
    links: {
      linkedin: "https://www.linkedin.com/in/hugh-carnegie-868962b1/",
      x: "https://x.com/CarnegieHu45819",
    },
  },
  {
    name: "Cher Truman",
    role: "Marketing & Growth",
    location: "London",
    badge: "Joining soon",
    bio: "Brand and media strategist with international clients including Pinterest and Müller. Currently at WPP Media. MSc Leeds, Executive Branding at Oxford.",
    photo: "/team-cher.png",
    initials: "CT",
    photoPosition: "center 30%",
    links: { linkedin: "https://www.linkedin.com/in/cher-truman-a6aa49167/", x: "https://x.com/TrumanCher59196" },
  },
  { name: "Milo Rowse", role: "Customer Success", location: "London", bio: "Head of Customer Success at XENI. Background in AI products, user research, and partnerships in EdTech.", photo: "/team-milo.jpg", initials: "MR", photoPosition: "center 20%", links: { linkedin: "https://www.linkedin.com/in/milorowse/" } },
];

const ADVISORS: Member[] = [
  {
    name: "George Yang",
    role: "Strategic Advisor",
    location: "Singapore",
    bio: "Investor in Anthropic, Stripe, xAI, Databricks, Notion.AI. Former Partner at CVC. $10B AUM, $20B+ transaction experience.",
    photo: "/team-george.png",
    initials: "GY",
    photoPosition: "center 35%",
    links: {
      linkedin: "https://www.linkedin.com/in/george-y-7a96a76/",
    },
  },
  {
    name: "",
    role: "",
    photo: null,
    initials: "",
    placeholder: true,
  },
];

function TeamCard({ member }: { member: Member }) {
  if (member.placeholder) {
    return (
      <div className="border border-dashed border-[var(--color-border)] bg-transparent overflow-hidden flex flex-col items-center justify-center text-center min-h-[280px]">
        <div className="w-10 h-10 rounded-full border border-dashed border-[var(--color-border)] flex items-center justify-center mb-3">
          <span className="text-lg text-[var(--color-fg-tertiary)]">+</span>
        </div>
        <span className="text-xs font-[family-name:var(--font-mono)] text-[var(--color-fg-tertiary)]">
          Expanding
        </span>
      </div>
    );
  }

  return (
    <div className="border border-[var(--color-border)] bg-[var(--color-surface)] overflow-hidden flex flex-col">
      <div className="relative">
        {member.photo ? (
          <img
            src={member.photo}
            alt={member.name}
            className="w-full aspect-[4/3] bg-[var(--color-bg-secondary)] object-cover"
            style={{ objectPosition: member.photoPosition ?? "center" }}
            loading="lazy"
          />
        ) : (
          <div className="w-full aspect-[4/3] bg-[var(--color-bg-secondary)] flex items-center justify-center">
            <span className="text-2xl font-[family-name:var(--font-mono)] font-medium text-[var(--color-fg-tertiary)]">
              {member.initials}
            </span>
          </div>
        )}
        {member.badge && (
          <span className="absolute top-3 right-3 text-[9px] font-[family-name:var(--font-mono)] text-amber-500 bg-[var(--color-bg)]/80 backdrop-blur-sm px-2 py-1 rounded-full border border-amber-500/20">
            {member.badge}
          </span>
        )}
      </div>
      <div className="p-5 flex flex-col flex-1">
        {member.role && (
          <span className="text-[10px] font-[family-name:var(--font-mono)] tracking-widest uppercase text-[var(--color-fg-tertiary)] mb-2">
            {member.role}
          </span>
        )}
        <h3 className={`text-lg font-medium ${member.placeholder ? "text-[var(--color-fg-tertiary)]" : ""}`}>
          {member.name}
        </h3>
        {member.bio && (
          <p className="text-sm text-[var(--color-fg-secondary)] leading-relaxed mt-2 mb-3">
            {member.bio}
          </p>
        )}
        {(member.links || member.location) && !member.placeholder && (
          <div className="flex items-center justify-between pt-3 mt-auto border-t border-[var(--color-border)]">
            <div className="flex gap-3">
              {member.links && Object.entries(member.links).map(([platform, url]) => {
                const Icon = ICON_MAP[platform];
                if (!Icon) return null;
                return (
                  <a
                    key={platform}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[var(--color-fg-tertiary)] hover:text-[var(--color-fg)] transition-colors"
                    aria-label={`${member.name} on ${platform}`}
                  >
                    <Icon />
                  </a>
                );
              })}
            </div>
            {member.location && (
              <span className="text-[9px] font-[family-name:var(--font-mono)] tracking-widest uppercase text-[var(--color-fg-tertiary)] border border-[var(--color-border)] px-2 py-0.5 rounded">
                {member.location}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function TeamFullSection() {
  return (
    <ScrollSection id="team" title="The team" compact>
      {/* Executive team */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {EXEC_TEAM.map((m) => (
          <TeamCard key={m.name} member={m} />
        ))}
      </div>

      {/* Advisory board */}
      <div className="text-[10px] font-[family-name:var(--font-mono)] tracking-widest uppercase text-[var(--color-fg-tertiary)] mt-10 mb-4">
        Advisory board
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {ADVISORS.map((m) => (
          <TeamCard key={m.name} member={m} />
        ))}
      </div>

      <AgentActivityFeed />

      <a
        href="#careers"
        className="inline-block text-sm font-[family-name:var(--font-mono)] text-[var(--color-fg-secondary)] hover:text-[var(--color-fg)] transition-colors"
      >
        View open positions →
      </a>
    </ScrollSection>
  );
}
